/**
 * Feedback Survey Component
 *
 * A non-intrusive satisfaction survey that appears:
 * - After many chats (global Marta feedback)
 * - When opening a new chat (global feedback)
 * - After a long chat (chat-specific feedback)
 */

import React, { useState, useEffect } from 'react';

export type FeedbackRating = 'bad' | 'fine' | 'good' | 'great';

export interface FeedbackData {
  rating: FeedbackRating;
  comment?: string;
  feedbackType: 'global' | 'chat_specific';
  chatId?: string;
  messageCount?: number;
  timestamp: string;
  userId?: string;
}

interface FeedbackSurveyProps {
  feedbackType: 'global' | 'chat_specific';
  chatId?: string;
  messageCount?: number;
  onSubmit: (feedback: FeedbackData) => void;
  onDismiss: () => void;
  className?: string;
}

const RATING_OPTIONS: { value: FeedbackRating; label: string; emoji: string; color: string }[] = [
  { value: 'bad', label: 'Bad', emoji: '😞', color: 'hover:bg-red-500/20 hover:border-red-500' },
  { value: 'fine', label: 'Fine', emoji: '😐', color: 'hover:bg-yellow-500/20 hover:border-yellow-500' },
  { value: 'good', label: 'Good', emoji: '🙂', color: 'hover:bg-blue-500/20 hover:border-blue-500' },
  { value: 'great', label: 'Great', emoji: '😄', color: 'hover:bg-green-500/20 hover:border-green-500' },
];

const SELECTED_COLORS: Record<FeedbackRating, string> = {
  bad: 'bg-red-500/20 border-red-500 text-red-400',
  fine: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
  good: 'bg-blue-500/20 border-blue-500 text-blue-400',
  great: 'bg-green-500/20 border-green-500 text-green-400',
};

export function FeedbackSurvey({
  feedbackType,
  chatId,
  messageCount,
  onSubmit,
  onDismiss,
  className = '',
}: FeedbackSurveyProps) {
  const [selectedRating, setSelectedRating] = useState<FeedbackRating | null>(null);
  const [comment, setComment] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-expand comment area when rating is selected
  useEffect(() => {
    if (selectedRating) {
      setIsExpanded(true);
    }
  }, [selectedRating]);

  const handleRatingClick = (rating: FeedbackRating) => {
    if (selectedRating === rating) {
      // Double click on same rating = submit without comment
      handleSubmit(rating);
    } else {
      setSelectedRating(rating);
    }
  };

  const handleSubmit = async (ratingOverride?: FeedbackRating) => {
    const rating = ratingOverride || selectedRating;
    if (!rating) return;

    setIsSubmitting(true);

    const feedback: FeedbackData = {
      rating,
      comment: comment.trim() || undefined,
      feedbackType,
      chatId,
      messageCount,
      timestamp: new Date().toISOString(),
    };

    await onSubmit(feedback);
    setIsSubmitting(false);
  };

  const hasComment = comment.trim().length > 0;

  return (
    <div className={`bg-dark-800/90 backdrop-blur-sm rounded-xl border border-dark-700 p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-dark-300">
          {feedbackType === 'global'
            ? "How would you rate Marta's performance?"
            : "How was this conversation?"}
        </p>
        <button
          onClick={onDismiss}
          className="text-dark-500 hover:text-dark-300 text-lg leading-none"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>

      {/* Rating Buttons */}
      <div className="flex gap-2 mb-3">
        {RATING_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => handleRatingClick(option.value)}
            className={`flex-1 flex flex-col items-center gap-1 py-2 px-3 rounded-lg border transition-all ${
              selectedRating === option.value
                ? SELECTED_COLORS[option.value]
                : `border-dark-600 text-dark-400 ${option.color}`
            }`}
          >
            <span className="text-xl">{option.emoji}</span>
            <span className="text-xs font-medium">{option.label}</span>
          </button>
        ))}
      </div>

      {/* Comment Area (expandable) */}
      {isExpanded && (
        <div className="space-y-3 animate-in slide-in-from-top-2 duration-200">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us more (optional)..."
            className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-dark-500 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            rows={2}
          />

          <div className="flex justify-end gap-2">
            <button
              onClick={onDismiss}
              className="px-3 py-1.5 text-sm text-dark-400 hover:text-white transition-colors"
            >
              Skip
            </button>
            <button
              onClick={() => handleSubmit()}
              disabled={!selectedRating || isSubmitting}
              className={`px-4 py-1.5 text-sm rounded-lg transition-all ${
                hasComment
                  ? 'bg-primary-600 hover:bg-primary-500 text-white shadow-lg shadow-primary-500/20'
                  : selectedRating
                  ? 'bg-dark-600 hover:bg-dark-500 text-dark-200'
                  : 'bg-dark-700 text-dark-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? 'Sending...' : hasComment ? 'Send Feedback' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {/* Quick submit hint */}
      {!isExpanded && selectedRating && (
        <p className="text-xs text-dark-500 text-center">
          Click again to submit, or add a comment
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Feedback Display Logic
// =============================================================================

interface FeedbackState {
  totalChats: number;
  totalMessages: number;
  lastFeedbackAt: string | null;
  feedbackCount: number;
}

const FEEDBACK_STORAGE_KEY = 'marta_feedback_state';

export function getFeedbackState(): FeedbackState {
  if (typeof window === 'undefined') {
    return { totalChats: 0, totalMessages: 0, lastFeedbackAt: null, feedbackCount: 0 };
  }

  try {
    const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}

  return { totalChats: 0, totalMessages: 0, lastFeedbackAt: null, feedbackCount: 0 };
}

export function updateFeedbackState(updates: Partial<FeedbackState>): void {
  if (typeof window === 'undefined') return;

  const current = getFeedbackState();
  const updated = { ...current, ...updates };
  localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(updated));
}

export function incrementChatCount(): void {
  const state = getFeedbackState();
  updateFeedbackState({ totalChats: state.totalChats + 1 });
}

export function incrementMessageCount(count: number = 1): void {
  const state = getFeedbackState();
  updateFeedbackState({ totalMessages: state.totalMessages + count });
}

export function recordFeedbackGiven(): void {
  const state = getFeedbackState();
  updateFeedbackState({
    lastFeedbackAt: new Date().toISOString(),
    feedbackCount: state.feedbackCount + 1,
  });
}

/**
 * Determines if feedback should be shown
 * Returns the type of feedback to show, or null if none
 */
export function shouldShowFeedback(currentMessageCount: number): 'global' | 'chat_specific' | null {
  const state = getFeedbackState();

  // Don't show feedback too frequently
  if (state.lastFeedbackAt) {
    const hoursSinceLastFeedback =
      (Date.now() - new Date(state.lastFeedbackAt).getTime()) / (1000 * 60 * 60);

    // Wait at least 24 hours between feedback requests
    if (hoursSinceLastFeedback < 24) {
      return null;
    }
  }

  // Show global feedback after many chats (every 10 chats after the first 5)
  if (state.totalChats >= 5 && state.totalChats % 10 === 0 && state.feedbackCount < Math.floor(state.totalChats / 10)) {
    return 'global';
  }

  // Show chat-specific feedback after a long conversation (15+ messages)
  if (currentMessageCount >= 15 && currentMessageCount % 15 === 0) {
    return 'chat_specific';
  }

  // Show global feedback after first 3 chats
  if (state.totalChats === 3 && state.feedbackCount === 0) {
    return 'global';
  }

  return null;
}

export default FeedbackSurvey;
